import { ChevronDown, PenSquare, CircleUserRound } from "lucide-react"
import { Button } from "../Button"
import { Dropdown } from "../Dropdown"
import { useTranslations } from "modules/i18n/hooks/useTranslations"
import { useViewer } from "common/hooks/data/useViewer"
import { usePageProps } from "common/hooks/data/usePageProps"
import { useRouter } from "common/lib/router"
import type { AppProps } from "common/lib/getAppServerSideProps"

const ANALYTICS_URL =
  "https://analytics.theconversation.com?utm_source=theconversation.com&utm_medium=website&utm_campaign=account_menu"

/**
 * Component responsible to render account menu actions, such as Button to the viewer profile, sign out Button, etc.
 * In case the viewer is not logged in, it will render a "sign in" button instead.
 */
export const AccountMenu = () => {
  const { viewer, isEditor } = useViewer()
  const { editUrl } = usePageProps<AppProps>()
  const router = useRouter()
  const t = useTranslations("layout.header.user_navbar")

  const user = viewer?.user
  const analytics = viewer?.user?.allow.analytics
  const moderateComments = viewer?.user?.allow.moderateComments
  const commentNotificationCount = viewer?.user?.commentNotificationCount
  const newsFeedCount = viewer?.user?.newsFeedCount
  const shouldShowAdminDivider = isEditor && editUrl

  if (!user) {
    return (
      <>
        <div className="hidden h-full lg:flex">
          <Button href={`/sign_in?return_to=${router.asPath}`} withDivider>
            {t("sign_in")}
          </Button>
        </div>
        <div className="flex h-full lg:hidden">
          <Button href={`/sign_in?return_to=${router.asPath}`} type="bold" ariaLabel={t("sign_in")}>
            <CircleUserRound className="w-6" />
          </Button>
        </div>
      </>
    )
  }

  const { avatar, name, id } = user

  const dropdownOverlay = (
    <>
      {analytics && (
        <Button href={ANALYTICS_URL} role="menuitem">
          {t("analytics")}
        </Button>
      )}
      {moderateComments && (
        <Button href="/editorial/comments/editor" role="menuitem">
          {t("moderate_comments")}
          {!!commentNotificationCount && (
            <span className="ml-3 inline-block size-4 rounded-full bg-yellow-500"></span>
          )}
        </Button>
      )}
      <div className="flex flex-col md:hidden">
        {isEditor && user?.id && (
          <Button role="menuitem" href={`/editorial/dashboards/${user.id}`}>
            {t("dashboard")}
          </Button>
        )}
      </div>
      <Button href={`/profiles/${id}/news`} role="menuitem">
        {t("profile")}
      </Button>
      <Button href="/sign_out" role="menuitem">
        {t("sign_out")}
      </Button>
      {shouldShowAdminDivider && (
        <>
          <div className="my-1 border-b border-gray-100" role="presentation" />
          <p className="text-2xs mt-3 mb-1 px-2.5 font-bold">{t("admin")}</p>
        </>
      )}
      {isEditor && editUrl && (
        <Button href={editUrl} role="menuitem">
          <PenSquare className="w-6 pr-1" />
          {t("edit_page")}
        </Button>
      )}
    </>
  )

  return (
    <Dropdown overlay={dropdownOverlay} placement="right" wideOverlay>
      <Button type="icon">
        <div className="mr-1 hidden max-w-30 truncate font-sans text-xs sm:block xl:max-w-40">
          {name}
        </div>
        <ChevronDown className="mr-1 hidden size-4 md:block" />
        <div className="relative -mt-px">
          <img src={avatar} alt="" className="my-1 size-7 max-w-none rounded-full lg:size-9" />
          {!!newsFeedCount && (
            <div className="text-3xs absolute -top-0.5 -right-2 rounded-full bg-red-500 px-1.5 py-0.5 font-sans font-bold text-white ring-2 ring-white">
              {newsFeedCount}
            </div>
          )}
        </div>
      </Button>
    </Dropdown>
  )
}
